import React, { ReactNode, useEffect } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Notification } from '../services/notifications.service';
import { NOTIFICATIONS_KEY } from '../hooks/useNotifications';
import { usePreferences } from '../hooks/usePreferences';
import { getCachedPreferences } from '../lib/preferences';
import { useAuthStore } from '../store/auth.store';

interface NotificationProviderProps {
  children: ReactNode;
}

// The API address is ".../api"; the socket lives on the same server, in the "notifications" namespace
const socketUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
  return `${apiUrl.replace(/\/api\/?$/, '')}/notifications`;
};

const TOAST_ICONS: Record<string, string> = { success: '✅', warning: '⚠️', error: '⛔' };

// Listens for notifications pushed by the server while someone is signed in. The bell, the
// dropdown and the notifications page read through react-query; this only tells them to refresh
// and shows a pop-up when the user wants one.
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userId = (useAuthStore((state) => state.user) as { id?: string } | null)?.id;

  // Keeps the copy of the preferences in the browser fresh for every page
  usePreferences(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const socket = io(socketUrl(), {
      auth: { token },
      // Polling first would be refused by servers behind a plain proxy; websocket works everywhere here
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });

    const refresh = () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });

    socket.on('notification:new', (notification: Notification) => {
      refresh();
      // Read at the moment it arrives, so a change in Settings applies straight away
      if (getCachedPreferences().notifications.popups) {
        toast(`${notification.title}\n${notification.message}`, {
          duration: 6000,
          icon: TOAST_ICONS[notification.type] ?? '🔔',
          style: { maxWidth: 420, whiteSpace: 'pre-line' },
        });
      }
    });
    socket.on('notification:stats', (stats) => {
      queryClient.setQueryData([...NOTIFICATIONS_KEY, 'stats'], stats);
    });

    return () => {
      socket.disconnect();
    };
    // A different user signing in gets a new connection
  }, [isAuthenticated, userId, queryClient]);

  return <>{children}</>;
};
